import React from 'react';
import useStyles from './styles';
import { AccountMenu, Searchbar } from 'molecules';
import {
  Hamburger,
  Logo,
  Avatar,
  BaseLink,
  User,
  Button,
  ThemeToggle,
} from '@elevator/ui';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import ListItemIcon from '@mui/material/ListItemIcon';
import * as IconMDI from 'mdi-material-ui';
import { APPBAR_ROUTES, ROUTES } from 'config/Nav';
import { RouteConfig } from 'types/routes';
import clsx from 'clsx';

const MobileAppbar = (props: MobileAppbarPropTypes) => {
  const { classes } = useStyles();

  const {
    handleSignoutClick,
    currentUser,
    searchInput,
    handleSearchInput,
    onSubmitSearchInput,
    handleSubmitClick,
  } = props;

  // Tracks whether the hamburger menu should be opened or closed
  const [menuIsOpen, setMenuIsOpen] = React.useState(false);
  // Tracks whether the user menu (logged-in or logged-out) should be opened or closed
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  /**
   * Handler for toggling the hamburger menu dropdown
   */
  const toggleMenu = (imperativeValue?: boolean) => {
    if (imperativeValue !== undefined) {
      setMenuIsOpen(imperativeValue);
      if (imperativeValue) setUserMenuOpen(false);
      return;
    }
    setMenuIsOpen((prev) => {
      const next = !prev;
      if (next) setUserMenuOpen(false);
      return next;
    });
  };

  /**
   * Handler for toggling the user menu (same menu for logged-in and logged-out)
   */
  const toggleUserMenu = (imperativeValue?: boolean) => {
    if (imperativeValue !== undefined) {
      setUserMenuOpen(imperativeValue);
      if (imperativeValue) setMenuIsOpen(false);
      return;
    }
    setUserMenuOpen((prev) => {
      const next = !prev;
      if (next) setMenuIsOpen(false);
      return next;
    });
  };

  /**
   * Handle interactions when a user clicks outside the menus and they are open
   */
  const handleClickAwayFromMenu = () => {
    menuIsOpen && toggleMenu(false);
    userMenuOpen && toggleUserMenu(false);
  };

  const renderNavListItem = (
    { navLinkText, to, external, target }: RouteConfig,
    index: number,
    collection: RouteConfig[],
  ) => {
    const isLastListItem = index === collection.length - 1;
    const isFirstListItem = index === 0;

    return (
      <Box key={index}>
        <ListItem
          component={BaseLink as any}
          href={to || '#'}
          external={external}
          target={target}
          underline="none"
          color="inherit"
          className={classes.listItem}
          onClick={() => toggleMenu(false)}
        >
          <ListItemText
            primary={navLinkText}
            slotProps={{ primary: { className: classes.listItem } }}
          />
        </ListItem>
      </Box>
    );
  };

  /**
   * Renders the user menu (logged-in or logged-out state; AccountMenu handles both)
   */
  const renderUserMenuContent = () => (
    <Paper elevation={8} className={classes.listMenuContainer}>
      <AccountMenu
        currentUser={currentUser}
        onClose={() => toggleUserMenu(false)}
        handleSignoutClick={handleSignoutClick}
        handleSubmitClick={handleSubmitClick}
      />
    </Paper>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAwayFromMenu}>
      <Box className={classes.mobileAppbarWrapper} data-component="Appbar">
        <Box className={classes.root}>
          <AppBar elevation={1} className={classes.mobileAppBar}>
            <Toolbar className={classes.toolbar}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                gap={1}
              >
                <Stack direction="row" alignItems="center" gap={2}>
                  <Hamburger
                    onClick={() => toggleMenu()}
                    isOpen={menuIsOpen}
                    className={classes.hamburger}
                  />
                  <Logo href="/" width={90} height={30} glitch={true} />
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <ThemeToggle size="small" color="inherit" />
                  {currentUser ? (
                    <IconButton
                      onClick={() => toggleUserMenu()}
                      color="inherit"
                      aria-label="Account of current user"
                      aria-controls="user-menu"
                      aria-haspopup="true"
                      aria-expanded={userMenuOpen ? 'true' : undefined}
                      className={classes.mobileUserMenuIconButton}
                    >
                      <Avatar
                        username={currentUser.firstName}
                        size="small"
                        src={currentUser.avatar?.sourceUrl}
                        alt={`${currentUser.preferredUsername} avatar`}
                      />
                    </IconButton>
                  ) : (
                    <>
                      {/*
                      <IconButton
                        onClick={() => toggleUserMenu()}
                        color="inherit"
                        aria-label="Account menu"
                        aria-controls="user-menu"
                        aria-haspopup="true"
                        aria-expanded={userMenuOpen ? 'true' : undefined}
                        className={classes.mobileUserMenuIconButton}
                      >
                        <IconMUI.Person />
                      </IconButton>
                      */}
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        href={ROUTES.AUTH.LOG_IN.to || '#'}
                        className={classes.mobileLoginButton}
                        suppressHydrationWarning
                      >
                        Log In
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </Toolbar>
          </AppBar>
          <Box className={classes.collapseMenuContainer}>
            <Collapse in={menuIsOpen}>
              <Paper elevation={2} className={classes.listMenuContainer}>
                <Searchbar
                  rootContainerClass={classes.mobileSearchBar}
                  searchInput={searchInput}
                  handleSearchInput={handleSearchInput}
                  onSubmit={(e: React.FormEvent<HTMLFormElement>) =>
                    onSubmitSearchInput(e, toggleMenu)
                  }
                />
                <List
                  component="nav"
                  aria-label="navigation options"
                  className={classes.list}
                >
                  {APPBAR_ROUTES.map((routeConfig, index, collection) =>
                    renderNavListItem(routeConfig, index, collection),
                  )}
                </List>
                <ListItem
                  className={clsx(classes.listItem, classes.mobileList, classes.lastItem)}
                >
                  <Button
                    className={classes.submitButton}
                    variant={'contained'}
                    size={'small'}
                    onClick={() => {
                      handleSubmitClick();
                      toggleMenu(false);
                    }}
                    fullWidth
                    aria-label="Submit new content"
                  >
                    <IconMDI.InboxArrowUp className={classes.submitIcon} />
                    Submit
                  </Button>
                </ListItem>
              </Paper>
            </Collapse>
          </Box>
          <Box className={classes.collapseMenuContainer}>
            <Collapse in={userMenuOpen}>{renderUserMenuContent()}</Collapse>
          </Box>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export type MobileAppbarPropTypes = {
  handleSignoutClick: () => void;
  currentUser: User | null;
  searchInput: string;
  handleSearchInput: (input: string) => void;
  onSubmitSearchInput: (event: React.FormEvent<HTMLFormElement>, cb?: () => void) => void;
  handleSubmitClick: () => void;
  /** Storybook only: toggles logged-in state in stories */
  isAuthenticated?: boolean;
  /** Storybook only: when true, story shows admin permission group */
  isStaff?: boolean;
};

export default MobileAppbar;
